import { useContext, useEffect, useMemo, useReducer } from 'react';
import { useQueryParam } from 'use-query-params';
import {
  ChatSessionInputContext, ChatSessionQueriesContext, ActiveChatSessionContext,
  ChatPageContextDataContext, ChatSessionSelectedQueryContext, ChatSessionFiltersContext,
  ChatSessionFileUploadsContext,
  ChatSessionKolSearchesContext,
  ChatSettingsContext,
  ChatSessionItemsContext,
} from '@/components/Chat/context';
import { ChatHistoryItemsContext, ChatHistoryManagerContext, ChatHistorySharedContext } from '@/components/Chat.History/context';
import { ContextItemParam } from '@/containers/Chat.Widget/context-item';
import { useAppSelector } from '@/store';
import { buildMessageItems, buildMessageQueries } from '@/utils';
import { ChatStateDispatchContext } from './context';
import { chatReducer, generateEmptyChatState } from './state';
import { useChatSettings } from './useChatSettings';

export type Props = {
  children: React.ReactNode;
};

export const ChatStateContainer = ({ children }: Props) => {
  const [queryparam] = useQueryParam('ctx', ContextItemParam);
  const features = useAppSelector(state => state.group.features);
  const settings = useChatSettings();

  const [state, dispatch] = useReducer(chatReducer, generateEmptyChatState({
    context: queryparam,
    features,
    settings,
  }));
  const context = useContext(ChatPageContextDataContext);

  const memoizedContext = useMemo(() => context, [context]);

  useEffect(() => {
    dispatch({
      type: 'reset-chat-state',
      payload: {
        context: memoizedContext,
      },
    });
  }, [memoizedContext, dispatch]);

  const selectedQuery = useMemo(() => {
    return state.session.queries[state.session.selectedQueryIdentifier];
  }, [state.session.queries, state.session.selectedQueryIdentifier]);

  const queries = useMemo(() => buildMessageQueries({
    selectedQueryIdentifier: selectedQuery?.identifier,
    queries: state.session.queries,
  }), [selectedQuery?.identifier, state.session.queries]);

  const items = useMemo(() => buildMessageItems({
    fileUploads: state.session.fileUploads,
    queries,
    kolSearches: state.session.kolSearches,
  }), [state.session.fileUploads, state.session.kolSearches, queries]);

  return (
    <ChatStateDispatchContext.Provider value={dispatch}>
      <ChatSessionKolSearchesContext.Provider value={state.session.kolSearches}>
        <ChatSessionQueriesContext.Provider value={state.session.queries}>
          <ChatSessionInputContext.Provider value={state.session.input}>
            <ChatSessionFiltersContext.Provider value={state.session.filters}>
              <ActiveChatSessionContext.Provider value={state.session.chatInstance}>
                <ChatSessionSelectedQueryContext.Provider value={selectedQuery}>
                  <ChatHistoryItemsContext.Provider value={state.history.items}>
                    <ChatHistorySharedContext.Provider value={state.history.shared}>
                      <ChatHistoryManagerContext.Provider value={state.history.manager}>
                        <ChatSessionFileUploadsContext.Provider value={state.session.fileUploads}>
                          <ChatSettingsContext.Provider value={settings}>
                            <ChatSessionItemsContext.Provider value={items}>
                              {children}
                            </ChatSessionItemsContext.Provider>
                          </ChatSettingsContext.Provider>
                        </ChatSessionFileUploadsContext.Provider>
                      </ChatHistoryManagerContext.Provider>
                    </ChatHistorySharedContext.Provider>
                  </ChatHistoryItemsContext.Provider>
                </ChatSessionSelectedQueryContext.Provider>
              </ActiveChatSessionContext.Provider>
            </ChatSessionFiltersContext.Provider>
          </ChatSessionInputContext.Provider>
        </ChatSessionQueriesContext.Provider>
      </ChatSessionKolSearchesContext.Provider>
    </ChatStateDispatchContext.Provider>
  );
};